import React, { useState, useEffect } from "react";
import { Form, Select, Row, Col, theme } from "antd";
import { useTranslation } from "react-i18next";
import { ICategory } from "../../../models/category/response";
import { ITrademark } from "../../../models/trademark/response";

const { Option } = Select;

export interface FilterValues {
    keyword?: string;
    mainCategoryId?: number;
    parentCategoryId?: number;
    childCategoryId?: number;
    trademarkId?: number;
    available?: boolean | null;
}

interface Props {
    categories: ICategory[];
    trademarks: ITrademark[];
    initialValues?: FilterValues;
    onFilterChange: (filters: FilterValues) => void;
    formId?: string;
}

const ProductsFilter: React.FC<Props> = ({
    categories,
    trademarks,
    initialValues = {},
    onFilterChange,
    formId,
}) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();

    const [mainCat, setMainCat] = useState<number | undefined>(initialValues.mainCategoryId);
    const [parentCat, setParentCat] = useState<number | undefined>(initialValues.parentCategoryId);

    // Update form values when initialValues change (e.g. on reset)
    useEffect(() => {
        form.setFieldsValue({
            mainCategoryId: initialValues.mainCategoryId,
            parentCategoryId: initialValues.parentCategoryId,
            childCategoryId: initialValues.childCategoryId,
            trademarkId: initialValues.trademarkId,
            available: initialValues.available === true ? "true" : initialValues.available === false ? "false" : undefined,
        });
        setMainCat(initialValues.mainCategoryId);
        setParentCat(initialValues.parentCategoryId);
    }, [initialValues, form]);

    const handleSubmit = (values: any) => {
        const filters: FilterValues = {
            mainCategoryId: values.mainCategoryId,
            parentCategoryId: values.parentCategoryId,
            childCategoryId: values.childCategoryId,
            trademarkId: values.trademarkId,
            available: values.available === "true" ? true : values.available === "false" ? false : null,
        };



        onFilterChange(filters);
    };

    const handleMainCategoryChange = (value: number | undefined) => {
        setMainCat(value);
        setParentCat(undefined);
        form.setFieldsValue({ parentCategoryId: undefined, childCategoryId: undefined });
    };

    const handleParentCategoryChange = (value: number | undefined) => {
        setParentCat(value);
        form.setFieldsValue({ childCategoryId: undefined });
    };

    // Get filtered categories for each level
    const mainCategories = categories
        .filter((cat) => cat?.parent === null && cat?.grand === null)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

    const parentCategories = categories
        .filter((cat) =>
            mainCat
                ? cat?.parent === null && cat?.grand?.id === mainCat
                : cat?.parent === null && cat?.grand?.id !== null
        )
        .sort((a, b) => (a.order || 0) - (b.order || 0));

    const childCategories = categories
        .filter((cat) =>
            parentCat
                ? cat?.parent?.id === parentCat
                : cat?.parent?.id !== null
        )
        .sort((a, b) => (a.order || 0) - (b.order || 0));

    return (
        <Form form={form} layout="vertical" id={formId} onFinish={handleSubmit}>
            <Row gutter={[16, 16]}>
                {/* Main Category */}
                <Col xs={24} sm={12} md={8} lg={6}>
                    <Form.Item
                        name="mainCategoryId"
                        label={t("main_category")}
                        style={{ marginBottom: 0 }}
                    >
                        <Select
                            placeholder={t("select_main_category")}
                            onChange={handleMainCategoryChange}
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            size="large"
                        >
                            {mainCategories.map((cat) => (
                                <Option key={cat.id} value={cat.id}>
                                    {cat.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>

                {/* Parent Category */}
                <Col xs={24} sm={12} md={8} lg={6}>
                    <Form.Item
                        name="parentCategoryId"
                        label={t("parent_category")}
                        style={{ marginBottom: 0 }}
                    >
                        <Select
                            placeholder={t("select_parent_category")}
                            onChange={handleParentCategoryChange}
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            disabled={!mainCat && parentCategories.length === 0}
                            size="large"
                        >
                            {parentCategories.map((cat) => (
                                <Option key={cat.id} value={cat.id}>
                                    {cat.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>

                {/* Child Category */}
                <Col xs={24} sm={12} md={8} lg={6}>
                    <Form.Item
                        name="childCategoryId"
                        label={t("child_category")}
                        style={{ marginBottom: 0 }}
                    >
                        <Select
                            placeholder={t("select_child_category")}
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            disabled={!parentCat && childCategories.length === 0}
                            size="large"
                        >
                            {childCategories.map((cat) => (
                                <Option key={cat.id} value={cat.id}>
                                    {cat.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>

                {/* Trademark */}
                <Col xs={24} sm={12} md={8} lg={6}>
                    <Form.Item
                        name="trademarkId"
                        label={t("trademark")}
                        style={{ marginBottom: 0 }}
                    >
                        <Select
                            placeholder={t("select_trademark")}
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            size="large"
                        >
                            {trademarks.map((trademark) => (
                                <Option key={trademark.id} value={trademark.id}>
                                    {trademark.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>

                {/* Availability */}
                <Col xs={24} sm={12} md={8} lg={6}>
                    <Form.Item
                        name="available"
                        label={t("select_availability")}
                        style={{ marginBottom: 0 }}
                    >
                        <Select
                            placeholder={t("select_availability")}
                            allowClear
                            size="large"
                        >
                            <Option value="true">{t("available")}</Option>
                            <Option value="false">{t("unavailable")}</Option>
                        </Select>
                    </Form.Item>
                </Col>
            </Row>
        </Form>
    );
};

export default ProductsFilter;
